import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import { employeeService, departmentService, reportingManagerService } from "../services/apiService.jsx";
import "./Dashboard.css"; // or a shared form CSS

const CreateEmployee = () => {
  const navigate = useNavigate();
  const formRef = useRef();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState(""); // 'success' or 'error'
  const [notificationMessage, setNotificationMessage] = useState("");

  const [departments, setDepartments] = useState([]);
  const [reportingManagers, setReportingManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDesignation, setSelectedDesignation] = useState("");
  const [customDesignation, setCustomDesignation] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [customDepartment, setCustomDepartment] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Refs for date inputs to trigger picker
  const dobRef = useRef(null);
  const joiningDateRef = useRef(null);

  useEffect(() => {
    fetchFormData();
  }, []);

  const handleDateClick = (ref) => {
    if (ref.current) {
      ref.current.showPicker();
    }
  };

  const fetchFormData = async () => {
    setLoading(true);
    try {
      const [deptsResponse, managersResponse] = await Promise.all([
        departmentService.getAllDepartments(),
        reportingManagerService.getAllReportingManagers()
      ]);

      if (deptsResponse?.success && deptsResponse.data) {
        setDepartments(deptsResponse.data);
      }
      if (managersResponse?.success && managersResponse.data) {
        setReportingManagers(managersResponse.data);
      }
    } catch (error) {
      console.error("Error fetching form data:", error);
      showNotificationMessage("error", "Failed to load departments and reporting managers. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const showNotificationMessage = (type, message) => {
    setNotificationType(type);
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Track Designation changes
    if (name === "Designation") {
      setSelectedDesignation(value);
      if (value !== "Others") {
        setCustomDesignation(""); // Clear custom designation if not "Others"
      }
    }
    
    // Track custom designation input
    if (name === "customDesignation") {
      setCustomDesignation(value);
    }

    // Track Department changes
    if (name === "department_name") {
      setSelectedDepartment(value);
      if (value !== "Others") {
        setCustomDepartment(""); // Clear custom department if not "Others"
      }
    }

    // Track custom department input
    if (name === "customDepartment") {
      setCustomDepartment(value);
    }
    
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = (formData) => {
    const errors = {};
    if (!formData.get("first_name")) errors.first_name = "First name is required";
    if (!formData.get("last_name")) errors.last_name = "Last name is required";
    if (!formData.get("Designation")) errors.Designation = "Designation is required";
    
    // Validate custom designation if "Others" is selected
    if (formData.get("Designation") === "Others") {
      if (!customDesignation.trim()) {
        errors.customDesignation = "Please enter a custom designation";
      }
    }
    
    if (!formData.get("email_id")) errors.email_id = "Email ID is required";
    if (!formData.get("Contact_Number")) errors.Contact_Number = "Mobile number is required";
    if (!formData.get("birthday")) errors.birthday = "Date of birth is required";
    if (!formData.get("location")) errors.location = "Location is required";
    if (!formData.get("department_name")) errors.department_name = "Department is required";
    
    // Validate custom department if "Others" is selected
    if (formData.get("department_name") === "Others") {
      if (!customDepartment.trim()) {
        errors.customDepartment = "Please enter a custom department";
      }
    }
    
    if (!formData.get("reporting_manager_id")) errors.reporting_manager_id = "Reporting manager is required";
    if (!formData.get("Joiningdate")) errors.Joiningdate = "Joining date is required";
    if (!formData.get("status")) errors.status = "Status is required";
    if (!formData.get("profile_photo")) errors.profile_photo = "Profile photo is required";
    if (!formData.get("Username")) errors.Username = "Username is required";
    if (!formData.get("password")) errors.password = "Password is required";
    if (!formData.get("role")) errors.role = "Role is required";

    // Additional validations
    const empCode = formData.get("employee_id");
    if (empCode && !/^\d{5}$/.test(empCode)) {
      errors.employee_id = "Employee Code must be exactly 5 digits";
    }
    const phone = formData.get("Contact_Number");
    if (phone && !/^\d{10}$/.test(phone)) {
      errors.Contact_Number = "Mobile number must be 10 digits";
    }
    const password = formData.get("password");
    if (password) {
      if (password.length < 8) {
        errors.password = "Password must be at least 8 characters";
      } else if (!/[A-Z]/.test(password)) {
        errors.password = "Password must contain at least one uppercase letter";
      } else if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
        errors.password = "Password must contain at least one special character (!@#$%^&*...)";
      }
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);

    // Clear previous validation errors
    setValidationErrors({});

    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      showNotificationMessage("error", "Please fix the validation errors");
      return;
    }

    setIsSubmitting(true);

    try {
      // Map field names to backend expectations
      const correctedFormData = new FormData();
      correctedFormData.append("employee_code", formData.get("employee_id"));
      correctedFormData.append("username", formData.get("Username"));
      correctedFormData.append("password", formData.get("password"));
      correctedFormData.append("phone", formData.get("Contact_Number"));
      
      // Use custom designation if "Others" is selected, otherwise use the selected value
      const designationValue = formData.get("Designation") === "Others" 
        ? customDesignation 
        : formData.get("Designation");
      correctedFormData.append("designation", designationValue);
      
      correctedFormData.append("first_name", formData.get("first_name"));
      correctedFormData.append("last_name", formData.get("last_name"));
      correctedFormData.append("email_id", formData.get("email_id"));
      correctedFormData.append("birthday", formData.get("birthday"));
      correctedFormData.append("location", formData.get("location"));
      
      // Use custom department if "Others" is selected, otherwise use the selected value
      const departmentValue = formData.get("department_name") === "Others"
        ? customDepartment
        : formData.get("department_name");
      correctedFormData.append("department_name", departmentValue);
      
      correctedFormData.append("reporting_manager_id", formData.get("reporting_manager_id"));
      correctedFormData.append("joining_date", formData.get("Joiningdate"));
      correctedFormData.append("status", formData.get("status"));
      correctedFormData.append("role", formData.get("role"));

      const profilePhoto = formData.get("profile_photo");
      if (profilePhoto) {
        correctedFormData.append("profile_photo", profilePhoto);
      }

      await employeeService.createEmployee(correctedFormData);
      showNotificationMessage("success", "Employee created successfully!");
      form.reset();
      setSelectedDesignation("");
      setCustomDesignation("");
      setSelectedDepartment("");
      setCustomDepartment("");
      setTimeout(() => navigate("/employees"), 2000);
    } catch (error) {
      console.error("Error creating employee:", error);
      let errorMsg = "Failed to create employee";
      if (error.response) {
        if (error.response.status === 400) {
          const errorData = error.response.data;
          if (errorData.errors) {
            if (Array.isArray(errorData.errors)) {
              const fieldErrors = {};
              errorData.errors.forEach((err) => {
                // Map backend field names to frontend field names
                let fieldName = err.field;
                if (fieldName === 'phone' || fieldName === 'Contact_Number') {
                  fieldName = 'Contact_Number';
                }
                if (fieldName === 'Joiningdate' || fieldName === 'joining_date') {
                  fieldName = 'Joiningdate';
                }
                fieldErrors[fieldName] = err.message;
              });
              setValidationErrors(fieldErrors);
            } else {
              setValidationErrors(errorData.errors);
            }
            console.log("Validation errors:", errorData.errors);
            showNotificationMessage("error", "Validation failed. Please check the form.");
            setIsSubmitting(false);
            return;
          } else {
            errorMsg = errorData.message || "Validation error";
          }
        } else if (error.response.status === 401) {
          errorMsg = "You are not authorized. Please login again.";
        } else if (error.response.status === 403) {
          errorMsg = "You don't have permission to create employees.";
        } else {
          errorMsg = error.response.data?.message || errorMsg;
        }
      } else if (error.request) {
        errorMsg = "No response from server. Please check your internet connection.";
      }
      showNotificationMessage("error", errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Notification Popup */}
      {showNotification && (
        <div
          className={`notification-popup ${notificationType === "success" ? "notification-success" : "notification-error"}`}
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 9999,
            padding: "12px 16px",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            minWidth: "280px",
            animation: "slideIn 0.3s ease",
            fontSize: "0.9rem",
          }}
        >
          <i className={`bi ${notificationType === "success" ? "bi-check-circle-fill" : "bi-exclamation-triangle-fill"}`}></i>
          <span style={{ flex: 1 }}>{notificationMessage}</span>
          <button
            onClick={() => setShowNotification(false)}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.1rem",
              cursor: "pointer",
              padding: "0 3px",
            }}
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
      )}

      {/* Header with back button */}
      <div className="d-flex align-items-center" style={{ marginBottom: "1rem" }}>
        <button
          onClick={() => navigate("/employees")}
          className="btn btn-link text-dark p-0 me-2"
          style={{ fontSize: "1.8rem", lineHeight: 1, textDecoration: "none" }}
          title="Go back to employees list"
        >
          <i className="bi bi-arrow-left"></i>
        </button>
        <h2 style={{ margin: 0, fontSize: "1.8rem", fontWeight: 500, color: "var(--secondary)" }}>Create Employee</h2>
      </div>

      {loading && (
        <div className="alert alert-info py-2 mb-3" style={{ fontSize: "0.9rem" }}>
          Loading departments and managers...
        </div>
      )}

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <form
                  id="employeeForm"
                  ref={formRef}
                  noValidate
                  encType="multipart/form-data"
                  onSubmit={handleSubmit}
                >
                  <div className="row g-3">
                    {/* First Name */}
                    <div className="col-md-3">
                      <label htmlFor="inputFirstName" className="form-label">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        className={`form-control form-control-sm ${validationErrors.first_name ? "is-invalid" : ""}`}
                        id="inputFirstName"
                        placeholder="Enter first name"
                        onChange={handleInputChange}
                        required
                      />
                      {validationErrors.first_name && (
                        <div className="invalid-feedback">{validationErrors.first_name}</div>
                      )}
                    </div>

                    {/* Last Name */}
                    <div className="col-md-3">
                      <label htmlFor="inputLastName" className="form-label">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        className={`form-control form-control-sm ${validationErrors.last_name ? "is-invalid" : ""}`}
                        id="inputLastName"
                        placeholder="Enter last name"
                        onChange={handleInputChange}
                        required
                      />
                      {validationErrors.last_name && (
                        <div className="invalid-feedback">{validationErrors.last_name}</div>
                      )}
                    </div>

                    {/* Designation */}
                    <div className="col-md-3">
                      <label htmlFor="inputDesignation" className="form-label">
                        Designation <span className="text-danger">*</span>
                      </label>
                      <select
                        name="Designation"
                        className={`form-select form-select-sm ${validationErrors.Designation ? "is-invalid" : ""}`}
                        id="inputDesignation"
                        value={selectedDesignation}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Choose...</option>
                        <option>Manager</option>
                        <option>Software Developer</option>
                        <option>Operation</option>
                        <option>Data Analyst</option>
                        <option>Service Support</option>
                        <option>Support</option>
                        <option>Project Manager</option>
                        <option>Intern</option>
                        <option>Management Trainee</option>
                        <option>Project Engineer</option>
                        <option>E-Surveillance Executive</option>
                        <option>Office Boy</option>
                        <option>Asst. Manager</option>
                        <option>Others</option>
                      </select>
                      {validationErrors.Designation && (
                        <div className="invalid-feedback">{validationErrors.Designation}</div>
                      )}
                    </div>

                    {/* Custom Designation (shown when "Others" is selected) */}
                    {selectedDesignation === "Others" && (
                      <div className="col-md-3">
                        <label htmlFor="inputCustomDesignation" className="form-label">
                          Enter Designation <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          name="customDesignation"
                          className={`form-control form-control-sm ${validationErrors.customDesignation ? "is-invalid" : ""}`}
                          id="inputCustomDesignation"
                          placeholder="Enter custom designation"
                          value={customDesignation}
                          onChange={handleInputChange}
                          required
                        />
                        {validationErrors.customDesignation && (
                          <div className="invalid-feedback">{validationErrors.customDesignation}</div>
                        )}
                      </div>
                    )}

                    {/* Employee Code */}
                    <div className="col-md-3">
                      <label htmlFor="inputEmployeeCode" className="form-label">
                        Employee Code
                      </label>
                      <input
                        type="text"
                        name="employee_id"
                        className={`form-control form-control-sm ${validationErrors.employee_id ? "is-invalid" : ""}`}
                        id="inputEmployeeCode"
                        placeholder="5 digits (optional)"
                        maxLength="5"
                        pattern="[0-9]{5}"
                        onChange={handleInputChange}
                      />
                      {validationErrors.employee_id && (
                        <div className="invalid-feedback">{validationErrors.employee_id}</div>
                      )}
                    </div>

                    {/* Email ID */}
                    <div className="col-md-3">
                      <label htmlFor="inputEmail" className="form-label">
                        Email ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        name="email_id"
                        className={`form-control form-control-sm ${validationErrors.email_id ? "is-invalid" : ""}`}
                        id="inputEmail"
                        placeholder="Enter a valid email"
                        onChange={handleInputChange}
                        required
                      />
                      {validationErrors.email_id && (
                        <div className="invalid-feedback">{validationErrors.email_id}</div>
                      )}
                    </div>

                    {/* Mobile Number */}
                    <div className="col-md-3">
                      <label htmlFor="inputMobileNumber" className="form-label">
                        Mobile Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="tel"
                        name="Contact_Number"
                        className={`form-control form-control-sm ${validationErrors.Contact_Number ? "is-invalid" : ""}`}
                        id="inputMobileNumber"
                        pattern="[0-9]{10}"
                        maxLength="10"
                        placeholder="10-digit number"
                        onChange={handleInputChange}
                        required
                      />
                      {validationErrors.Contact_Number && (
                        <div className="invalid-feedback">{validationErrors.Contact_Number}</div>
                      )}
                    </div>

                    {/* Date of Birth */}
                    <div className="col-md-3">
                      <label htmlFor="inputDateOfBirth" className="form-label">
                        Date of Birth <span className="text-danger">*</span>
                      </label>
                      <div onClick={() => handleDateClick(dobRef)} style={{ cursor: "pointer" }}>
                        <input
                          type="date"
                          name="birthday"
                          className={`form-control form-control-sm ${validationErrors.birthday ? "is-invalid" : ""}`}
                          id="inputDateOfBirth"
                          ref={dobRef}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      {validationErrors.birthday && (
                        <div className="invalid-feedback">{validationErrors.birthday}</div>
                      )}
                    </div>

                    {/* Location */}
                    <div className="col-md-3">
                      <label htmlFor="inputLocation" className="form-label">
                        Location <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="location"
                        className={`form-control form-control-sm ${validationErrors.location ? "is-invalid" : ""}`}
                        id="inputLocation"
                        onChange={handleInputChange}
                        required
                      />
                      {validationErrors.location && (
                        <div className="invalid-feedback">{validationErrors.location}</div>
                      )}
                    </div>

                    {/* Department */}
                    <div className="col-md-3">
                      <label htmlFor="inputDepartment" className="form-label">
                        Department <span className="text-danger">*</span>
                      </label>
                      <select
                        name="department_name"
                        className={`form-select form-select-sm ${validationErrors.department_name ? "is-invalid" : ""}`}
                        id="inputDepartment"
                        value={selectedDepartment}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Department</option>
                        {departments.map((dept, idx) => (
                          <option key={idx} value={dept}>
                            {dept}
                          </option>
                        ))}
                        <option>Others</option>
                      </select>
                      {validationErrors.department_name && (
                        <div className="invalid-feedback">{validationErrors.department_name}</div>
                      )}
                    </div>

                    {/* Custom Department (shown when "Others" is selected) */}
                    {selectedDepartment === "Others" && (
                      <div className="col-md-3">
                        <label htmlFor="inputCustomDepartment" className="form-label">
                          Enter Department <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          name="customDepartment"
                          className={`form-control form-control-sm ${validationErrors.customDepartment ? "is-invalid" : ""}`}
                          id="inputCustomDepartment"
                          placeholder="Enter custom department"
                          value={customDepartment}
                          onChange={handleInputChange}
                          required
                        />
                        {validationErrors.customDepartment && (
                          <div className="invalid-feedback">{validationErrors.customDepartment}</div>
                        )}
                      </div>
                    )}

                    {/* Reporting Manager */}
                    <div className="col-md-3">
                      <label htmlFor="inputReportingManager" className="form-label">
                        Reporting Manager <span className="text-danger">*</span>
                      </label>
                      <select
                        name="reporting_manager_id"
                        className={`form-select form-select-sm ${validationErrors.reporting_manager_id ? "is-invalid" : ""}`}
                        id="inputReportingManager"
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Reporting Manager</option>
                        {reportingManagers.map((manager, idx) => (
                          <option key={idx} value={manager.employee_id}>
                            {manager.name} - {manager.department_name}
                          </option>
                        ))}
                      </select>
                      {validationErrors.reporting_manager_id && (
                        <div className="invalid-feedback">{validationErrors.reporting_manager_id}</div>
                      )}
                    </div>

                    {/* Joining Date */}
                    <div className="col-md-3">
                      <label htmlFor="inputJoiningDate" className="form-label">
                        Joining Date <span className="text-danger">*</span>
                      </label>
                      <div onClick={() => handleDateClick(joiningDateRef)} style={{ cursor: "pointer" }}>
                        <input
                          type="date"
                          name="Joiningdate"
                          className={`form-control form-control-sm ${validationErrors.Joiningdate ? "is-invalid" : ""}`}
                          id="inputJoiningDate"
                          ref={joiningDateRef}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      {validationErrors.Joiningdate && (
                        <div className="invalid-feedback">{validationErrors.Joiningdate}</div>
                      )}
                    </div>

                    {/* Status */}
                    <div className="col-md-3">
                      <label htmlFor="inputStatus" className="form-label">
                        Status <span className="text-danger">*</span>
                      </label>
                      <select
                        name="status"
                        className={`form-select form-select-sm ${validationErrors.status ? "is-invalid" : ""}`}
                        id="inputStatus"
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Choose...</option>
                        <option>Active</option>
                        <option>Inactive</option>
                      </select>
                      {validationErrors.status && (
                        <div className="invalid-feedback">{validationErrors.status}</div>
                      )}
                    </div>

                    {/* Profile Photo */}
                    <div className="col-md-3">
                      <label htmlFor="inputPhoto" className="form-label">
                        Upload Photo <span className="text-danger">*</span>
                      </label>
                      <input
                        type="file"
                        name="profile_photo"
                        className={`form-control form-control-sm ${validationErrors.profile_photo ? "is-invalid" : ""}`}
                        id="inputPhoto"
                        accept="image/*"
                        onChange={handleInputChange}
                        required
                      />
                      {validationErrors.profile_photo && (
                        <div className="invalid-feedback">{validationErrors.profile_photo}</div>
                      )}
                    </div>

                    {/* Username */}
                    <div className="col-md-3">
                      <label htmlFor="inputUsername" className="form-label">
                        Username <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="Username"
                        className={`form-control form-control-sm ${validationErrors.Username ? "is-invalid" : ""}`}
                        id="inputUsername"
                        onChange={handleInputChange}
                        required
                      />
                      {validationErrors.Username && (
                        <div className="invalid-feedback">{validationErrors.Username}</div>
                      )}
                    </div>

                    {/* Password */}
                    <div className="col-md-3">
                      <label htmlFor="inputPassword" className="form-label">
                        Password <span className="text-danger">*</span>
                      </label>
                      <div style={{ position: "relative" }}>
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          className={`form-control form-control-sm ${validationErrors.password ? "is-invalid" : ""}`}
                          id="inputPassword"
                          minLength="8"
                          onChange={handleInputChange}
                          style={{ paddingRight: "35px" }}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            position: "absolute",
                            right: "8px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#666",
                            padding: "2px 4px",
                            fontSize: "1.1rem"
                          }}
                          title={showPassword ? "Hide password" : "Show password"}
                        >
                          <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                        </button>
                      </div>
                      {validationErrors.password && (
                        <div className="invalid-feedback d-block">{validationErrors.password}</div>
                      )}
                    </div>

                    {/* Role */}
                    <div className="col-md-3">
                      <label htmlFor="inputRole" className="form-label">
                        Role <span className="text-danger">*</span>
                      </label>
                      <select
                        name="role"
                        className={`form-select form-select-sm ${validationErrors.role ? "is-invalid" : ""}`}
                        id="inputRole"
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Choose...</option>
                        <option>admin</option>
                        <option>HOD</option>
                        <option>team lead</option>
                        <option>employee</option>
                      </select>
                      {validationErrors.role && (
                        <div className="invalid-feedback">{validationErrors.role}</div>
                      )}
                    </div>

                    {/* Submit */}
                    <div className="col-12 d-flex justify-content-end gap-2 mt-3">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate("/employees")}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-success"
                        disabled={isSubmitting || loading}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Creating...
                          </>
                        ) : (
                          "Create Employee"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CreateEmployee;